import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, Filter, Activity } from 'lucide-react';

interface ApprovalSession {
  id: string;
  question: string;
  token: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  responded_at: string | null;
}

export default function ApprovalDashboard() {
  const [sessions, setSessions] = useState<ApprovalSession[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Mock data for demonstration - in production, this would fetch from API
  useEffect(() => {
    const mockSessions: ApprovalSession[] = [
      {
        id: '1',
        question: 'May I add a real-time polling dashboard to monitor approval responses?',
        token: 'fbf7a4be-579c-42f7-87b3-fa2ffe091215',
        status: 'approved',
        created_at: '2025-09-26 01:09:23',
        responded_at: '2025-09-26 01:17:57'
      },
      {
        id: '2',
        question: 'Should we implement OAuth authentication for better security?',
        token: 'abc123',
        status: 'pending',
        created_at: '2025-09-26 01:25:00',
        responded_at: null
      },
      {
        id: '3',
        question: 'Can I refactor the database schema for better performance?',
        token: 'def456',
        status: 'rejected',
        created_at: '2025-09-26 01:00:00',
        responded_at: '2025-09-26 01:05:00'
      },
      {
        id: '4',
        question: 'Deploy the new feature to production environment?',
        token: 'ghi789',
        status: 'pending',
        created_at: '2025-09-26 01:28:00',
        responded_at: null
      },
      {
        id: '5',
        question: 'Add error handling to the payment processing module?',
        token: 'jkl012',
        status: 'approved',
        created_at: '2025-09-26 00:45:00',
        responded_at: '2025-09-26 00:50:00'
      }
    ];
    setSessions(mockSessions);
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setLastUpdate(new Date());
      // In production, fetch real data here
      console.log('Refreshing dashboard data...');
    }, 3000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const filteredSessions = sessions.filter(session => {
    if (filter === 'all') return true;
    return session.status === filter;
  });

  const stats = {
    total: sessions.length,
    pending: sessions.filter(s => s.status === 'pending').length,
    approved: sessions.filter(s => s.status === 'approved').length,
    rejected: sessions.filter(s => s.status === 'rejected').length,
    avgResponseTime: sessions
      .filter(s => s.responded_at)
      .reduce((acc, s) => {
        const created = new Date(s.created_at).getTime();
        const responded = new Date(s.responded_at!).getTime();
        return acc + (responded - created);
      }, 0) / sessions.filter(s => s.responded_at).length / 60000 || 0
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'rejected':
        return <XCircle className="text-red-500" size={20} />;
      default:
        return <Clock className="text-yellow-500 animate-pulse" size={20} />;
    }
  };

  const getTimeElapsed = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Approval Dashboard
            </h1>
            <p className="text-gray-300">
              Real-time monitoring of AI approval requests
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                autoRefresh
                  ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                  : 'bg-gray-500/20 text-gray-400 border border-gray-500/50'
              }`}
            >
              <RefreshCw className={autoRefresh ? 'animate-spin' : ''} size={16} />
              {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            </button>
            <div className="text-gray-400 text-sm">
              Last update: {lastUpdate.toLocaleTimeString()}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total</p>
                <p className="text-3xl font-bold text-white">{stats.total}</p>
              </div>
              <Activity className="text-blue-400" size={32} />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Pending</p>
                <p className="text-3xl font-bold text-yellow-400">{stats.pending}</p>
              </div>
              <Clock className="text-yellow-400" size={32} />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Approved</p>
                <p className="text-3xl font-bold text-green-400">{stats.approved}</p>
              </div>
              <CheckCircle className="text-green-400" size={32} />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Rejected</p>
                <p className="text-3xl font-bold text-red-400">{stats.rejected}</p>
              </div>
              <XCircle className="text-red-400" size={32} />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Avg Response</p>
                <p className="text-3xl font-bold text-white">{stats.avgResponseTime.toFixed(1)}m</p>
              </div>
              <Clock className="text-purple-400" size={32} />
            </div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-all ${
              filter === 'all'
                ? 'bg-blue-500 text-white'
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            All ({stats.total})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg transition-all ${
              filter === 'pending'
                ? 'bg-yellow-500 text-white'
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            Pending ({stats.pending})
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-lg transition-all ${
              filter === 'approved'
                ? 'bg-green-500 text-white'
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            Approved ({stats.approved})
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-4 py-2 rounded-lg transition-all ${
              filter === 'rejected'
                ? 'bg-red-500 text-white'
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            Rejected ({stats.rejected})
          </button>
        </div>

        {/* Sessions List */}
        <div className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden">
          <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/20 text-gray-400 text-sm font-semibold">
            <div className="col-span-1">Status</div>
            <div className="col-span-6">Question</div>
            <div className="col-span-2">Created</div>
            <div className="col-span-2">Responded</div>
            <div className="col-span-1">Duration</div>
          </div>

          {filteredSessions.map(session => (
            <div
              key={session.id}
              className="grid grid-cols-12 gap-4 p-4 border-b border-white/10 hover:bg-white/5 transition-all"
            >
              <div className="col-span-1">
                {getStatusIcon(session.status)}
              </div>
              <div className="col-span-6 text-white">
                {session.question}
              </div>
              <div className="col-span-2 text-gray-400">
                {getTimeElapsed(session.created_at)}
              </div>
              <div className="col-span-2 text-gray-400">
                {session.responded_at ? getTimeElapsed(session.responded_at) : '-'}
              </div>
              <div className="col-span-1 text-gray-400">
                {session.responded_at
                  ? `${Math.round(
                      (new Date(session.responded_at).getTime() -
                        new Date(session.created_at).getTime()) /
                        60000
                    )}m`
                  : '-'}
              </div>
            </div>
          ))}
        </div>

        {filteredSessions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">No {filter !== 'all' ? filter : ''} requests found</p>
          </div>
        )}
      </div>
    </div>
  );
}